import { useRef, useState } from 'react';
import { useLotPositions, type LotPositionItem } from '../lib/con-api';
import { PopoverPortal } from './PopoverPortal';

/**
 * Dropdown per scegliere una LotPosition con sfuso disponibile (residualQty > 0
 * by default). Mostra "lotCode | productCode | residuo".
 * Restituisce id + productId (utile al chiamante per filtrare gli SKU dopo).
 */
export function LotPositionPicker({
  value,
  valueLabel,
  onChange,
  disabled,
  minResidualQty = 1,
}: {
  value: string;
  valueLabel?: string | null;
  onChange: (item: LotPositionItem) => void;
  disabled?: boolean;
  minResidualQty?: number;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState('');
  const q = useLotPositions({
    pageSize: 100,
    minResidualQty,
    ...(search.trim() && { search: search.trim() }),
  });

  return (
    <div className="relative">
      <button
        ref={anchorRef}
        type="button"
        className="w-full rounded-md border border-slate-300 px-2 py-1 text-left text-xs"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
      >
        {valueLabel || (value ? value.slice(0, 8) + '…' : '— scegli posizione lotto —')}
      </button>
      <PopoverPortal anchorRef={anchorRef} open={open} onClose={() => setOpen(false)}>
        <div className="w-96 rounded-md border border-slate-300 bg-white p-2 shadow-lg">
          <input
            autoFocus
            type="text"
            className="mb-2 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
            placeholder="Cerca per lotto, prodotto, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="max-h-72 overflow-y-auto text-xs">
            {q.isLoading && <li className="p-1 text-slate-400">Caricamento...</li>}
            {q.data && q.data.items.length === 0 && (
              <li className="p-1 text-slate-400">Nessuna posizione con residuo &gt; {minResidualQty}g</li>
            )}
            {q.data?.items.map((it) => (
              <li
                key={it.id}
                className="cursor-pointer rounded p-1 hover:bg-slate-100"
                onClick={() => {
                  onChange(it);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <span className="font-mono text-slate-500">{it.lotCode}</span>
                <span className="ml-2 text-slate-400">|</span>
                {it.productDriveLegendId !== null && (
                  <span className="ml-2 inline-block min-w-[20px] rounded bg-brand-50 px-1 text-center font-mono text-[10px] font-semibold text-brand-700">
                    {it.productDriveLegendId}
                  </span>
                )}
                <span className="ml-1 font-medium">{it.productName}</span>
                <span className="ml-2 text-slate-500">{Number(it.residualQty).toFixed(0)}g</span>
              </li>
            ))}
          </ul>
        </div>
      </PopoverPortal>
    </div>
  );
}
